import React, { useState } from "react";
import {
  ArrowLeft,
  LayoutList,
  TableProperties,
  ScatterChart as ScatterIcon,
  FileDown,
  FileSpreadsheet,
  Pencil,
  Trash2,
} from "lucide-react";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "components/ui/toggle-group";
import CollectionListView from "./CollectionListView";
import CollectionTableView from "./CollectionTableView";
import CollectionGraphView from "./CollectionGraphView";

const CollectionDetailView = ({
  collection,
  onBack,
  onRename,
  onDelete,
  onExportCSV,
  onExportExcel,
}) => {
  const [view, setView] = useState("list");

  if (!collection) return null;

  const entityType =
    collection.entity_type || collection.entityType || "researchers";

  const formattedDate =
    collection.created_at || collection.createdAt
      ? new Date(
          collection.created_at || collection.createdAt
        ).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "Recently saved";

  return (
    <div className="flex flex-col w-full pb-12 animate-in fade-in duration-200">
      {/* Top Navigation */}
      <div className="flex items-center justify-between mb-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="gap-2 text-xs font-medium text-gray-600 hover:text-explore hover:bg-explore/5 -ml-2 h-8 px-2.5 transition-colors cursor-pointer"
        >
          <ArrowLeft size={15} />
          Back to saved searches
        </Button>
      </div>

      {/* Main Header Card */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-5 border-b border-gray-200 mb-6">
        <div>
          <div className="flex items-center gap-2.5 mb-1.5 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight m-0">
              {collection.name}
            </h1>
            <Badge
              variant="secondary"
              className="capitalize text-xs font-semibold px-2 py-0.5 bg-gray-100 text-gray-700"
            >
              {entityType}
            </Badge>
            <Badge
              variant="outline"
              className="text-xs px-2 py-0.5 text-explore border-explore/30 bg-explore/5 font-medium"
            >
              Top {collection.limit || 20}
            </Badge>
          </div>
          <p className="text-xs text-gray-500 m-0">
            <span>Saved on {formattedDate}</span>
            <span className="mx-2">•</span>
            <span className="font-medium text-gray-700">
              {collection.items?.length || 0}
            </span>{" "}
            total items stored in this snapshot
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          {onRename && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRename}
              className="h-8 px-3 text-xs gap-1.5 text-gray-700 hover:text-explore hover:border-explore/40 bg-white shadow-xs cursor-pointer"
            >
              <Pencil size={13} />
              Rename
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={onExportCSV}
            className="h-8 px-3 text-xs gap-1.5 text-gray-700 hover:text-gray-900 bg-white shadow-xs cursor-pointer"
          >
            <FileDown size={13} />
            Export CSV
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={onExportExcel}
            className="h-8 px-3 text-xs gap-1.5 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50/50 border-emerald-200/80 bg-white shadow-xs cursor-pointer"
          >
            <FileSpreadsheet size={13} />
            Export Excel
          </Button>

          {onDelete && (
            <Button
              variant="outline"
              size="sm"
              onClick={onDelete}
              className="h-8 px-3 text-xs gap-1.5 text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200 bg-white shadow-xs cursor-pointer"
            >
              <Trash2 size={13} />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* View Switcher Toolbar */}
      <div className="flex items-center justify-between mb-5">
        <ToggleGroup
          type="single"
          value={view}
          onValueChange={(val) => val && setView(val)}
          className="bg-gray-100 p-1 rounded-lg border border-gray-200/70"
        >
          <ToggleGroupItem
            value="list"
            className="h-8 px-3.5 text-xs data-[state=on]:bg-white data-[state=on]:text-explore data-[state=on]:shadow-xs rounded-md text-gray-600 font-medium transition-all cursor-pointer"
          >
            <LayoutList size={14} className="mr-1.5" />
            List View
          </ToggleGroupItem>
          <ToggleGroupItem
            value="table"
            className="h-8 px-3.5 text-xs data-[state=on]:bg-white data-[state=on]:text-explore data-[state=on]:shadow-xs rounded-md text-gray-600 font-medium transition-all cursor-pointer"
          >
            <TableProperties size={14} className="mr-1.5" />
            Table View
          </ToggleGroupItem>
          <ToggleGroupItem
            value="graph"
            className="h-8 px-3.5 text-xs data-[state=on]:bg-white data-[state=on]:text-explore data-[state=on]:shadow-xs rounded-md text-gray-600 font-medium transition-all cursor-pointer"
          >
            <ScatterIcon size={14} className="mr-1.5" />
            Graph View
          </ToggleGroupItem>
        </ToggleGroup>

        <span className="text-xs text-gray-400 hidden sm:inline-block">
          Showing {collection.items?.length || 0} results
        </span>
      </div>

      {/* Main Content Area */}
      <div className="w-full">
        {view === "list" ? (
          <CollectionListView
            items={collection.items || []}
            entityType={entityType}
          />
        ) : view === "table" ? (
          <CollectionTableView
            items={collection.items || []}
            entityType={entityType}
          />
        ) : (
          <CollectionGraphView
            items={collection.items || []}
            entityType={entityType}
          />
        )}
      </div>
    </div>
  );
};

export default CollectionDetailView;
